// ═══════════════════════════════════════════════════════════════════════
// DetailDrawer — generic row inspector (Commit 1b, editable)
// ═══════════════════════════════════════════════════════════════════════
// Mounted once in SchemaRenderer. Subscribes to useOsStore's drawer
// slice and renders whatever row + screen the store currently holds.
//
// Generic by construction: knows nothing about Deals/Tasks/Contacts
// specifically. Reads its field schema from screen.fields when present;
// falls back to inferring fields from the first non-internal keys on
// the row so every screen renders something useful even before SQL
// populates explicit field definitions.
//
// Editing (Commit 1b):
//   - Click any field value → enters edit mode with the matching editor
//   - Enter / blur saves; Esc cancels
//   - Save flow: optimistic update → PATCH /api/screen-data/:rowId →
//     bumpScreenRefresh on success, rollback on failure
//   - FieldUser is read-only (user picker arrives later) — small note shown
//   - Seed-data preview rows (non-UUID row ids) are read-only
//
// Layout:
//   - md and up: right-side slide-in, 480 px wide
//   - <768 px:    bottom-sheet, slide-up, full width
// ═══════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { X, Pencil, Loader2, Trash2 } from "lucide-react";
import { useOsStore } from "../../lib/os-store";
import type { ScreenField } from "@sloe/schema";
import { inferFields, renderField, renderFieldEditor } from "./field-renderers";

function rowTitle(row: Record<string, any> | null): string {
  if (!row) return "";
  return (
    row.title ||
    row.name ||
    row.label ||
    (row.id ? `#${row.id}` : "Untitled")
  );
}

// A "real" (editable) row has a UUID-shaped id, which signals it came from
// screen_data. Seed-preview rows use client-generated ids that don't match.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isEditableRow(row: Record<string, any> | null): boolean {
  return !!row && typeof row.id === "string" && UUID_RE.test(row.id);
}

// ─── Drawer component ─────────────────────────────────────────────────

export function DetailDrawer() {
  const { open, row, screen } = useOsStore((s) => s.drawer);
  const closeDrawer = useOsStore((s) => s.closeDrawer);
  const applyOptimisticUpdate = useOsStore((s) => s.applyOptimisticUpdate);
  const bumpScreenRefresh = useOsStore((s) => s.bumpScreenRefresh);
  const cachedRows = useOsStore((s) => s.screenRows.rows);

  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Esc dismisses the drawer — UNLESS an editor is open, in which case Esc
  // belongs to the editor (handled there) so we let it bubble back to us
  // only when the editor closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (deleteConfirmOpen) {
        setDeleteConfirmOpen(false);
        return;
      }
      if (editingKey === null) closeDrawer();
    };
    document.addEventListener("keydown", onKey);
    if (editingKey === null) closeBtnRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open, editingKey, deleteConfirmOpen, closeDrawer]);

  // Reset edit state when drawer closes or row changes.
  useEffect(() => {
    if (!open) {
      setEditingKey(null);
      setSaveError(null);
      setDeleteConfirmOpen(false);
      setDeleting(false);
    }
  }, [open, row?.id]);

  // Auto-clear save errors after 4s.
  useEffect(() => {
    if (!saveError) return;
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setSaveError(null), 4000);
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, [saveError]);

  const fields = useMemo<ScreenField[]>(() => {
    if (!row) return [];
    if (screen?.fields && screen.fields.length > 0) return screen.fields;
    return inferFields(row);
  }, [row, screen]);

  const editable = isEditableRow(row);

  /**
   * Run a single-field save with optimistic update + rollback. Closes over
   * `oldValue` so we can revert exactly to what the user saw before.
   */
  const saveField = useCallback(async (field: ScreenField, newValue: any, oldValue: any) => {
    if (!row?.id) return;
    setEditingKey(null);

    // Optimistic: paint the new value into the drawer's row immediately.
    applyOptimisticUpdate(field.key, newValue);

    // Build the full new data payload from the optimistically-updated row.
    // We strip the DB id off and merge the change — the server gets the
    // intended `data` jsonb in one PATCH.
    const { id: _id, ...currentData } = { ...row, [field.key]: newValue };

    try {
      const res = await fetch(`/api/screen-data/${encodeURIComponent(row.id)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: currentData }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      // Success — trigger the screen-level refetch so the table / kanban
      // pick up the authoritative server value.
      bumpScreenRefresh();
    } catch (err: any) {
      // Roll back the optimistic update.
      applyOptimisticUpdate(field.key, oldValue);
      setSaveError(err?.message || "Couldn't save · changes reverted");
    }
  }, [row, applyOptimisticUpdate, bumpScreenRefresh]);

  // CRUD-1 — delete the current row. Closes the drawer + refreshes the
  // screen on success. Confirmation is rendered in-app so the user knows
  // which record is being removed without leaving the Agentic Operating System experience.
  const deleteRow = useCallback(async () => {
    if (!row?.id) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/screen-data/${encodeURIComponent(row.id)}`, {
        method: "DELETE",
        credentials: "include",
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      closeDrawer();
      bumpScreenRefresh();
    } catch (err: any) {
      setSaveError(err?.message || "Couldn't delete · please try again");
      setDeleteConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  }, [row, closeDrawer, bumpScreenRefresh]);

  // Always mount (so animations play), conditionally show / hide.
  const renderingEmpty = !row || !screen;

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={closeDrawer}
        className={`fixed inset-0 z-40 bg-black/30 transition-opacity duration-200 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      {/* Panel — slides from right on md+, from bottom on mobile */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={renderingEmpty ? "Row details" : `Row details — ${rowTitle(row)}`}
        className={`
          fixed z-50 bg-white shadow-2xl flex flex-col
          transition-transform duration-300
          inset-x-0 bottom-0 top-[10vh] rounded-t-2xl
          md:inset-y-0 md:right-0 md:left-auto md:top-0 md:rounded-none md:rounded-l-2xl
          md:w-[480px]
          ${open
            ? "translate-y-0 md:translate-x-0"
            : "translate-y-full md:translate-y-0 md:translate-x-full"}
        `}
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-200">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              {screen?.label || "Details"}
            </div>
            <h2 className="text-lg font-bold text-slate-900 truncate mt-0.5">
              {renderingEmpty ? " " : rowTitle(row)}
            </h2>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={closeDrawer}
            aria-label="Close details"
            className="shrink-0 p-1.5 rounded-md text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
          >
            <X size={18} />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {renderingEmpty ? null : fields.length === 0 ? (
            <p className="text-sm text-slate-500">No fields to display.</p>
          ) : (
            <dl className="grid grid-cols-1 gap-y-5">
              {fields.map((field) => {
                const isEditing = editingKey === field.key;
                const isUserField = field.type === "user";
                const canEdit = editable && !isUserField;
                const value = row![field.key];
                // Dotted-underline affordance only makes sense for renderers
                // that produce plain text. Pills (select) and read-only user
                // fields skip it — a baseline underline behind a pill bg
                // looks broken; user fields shouldn't signal editability.
                const shouldUnderline =
                  canEdit && field.type !== "select" && field.type !== "user";

                return (
                  <div key={field.key}>
                    <dt className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                      {field.label}
                      {field.required && (
                        <span className="text-rose-500" aria-label="required">*</span>
                      )}
                    </dt>
                    <dd>
                      {isEditing && canEdit ? (
                        renderFieldEditor(
                          field,
                          value,
                          (newValue) => {
                            // Skip the save if nothing changed.
                            if (newValue === value) { setEditingKey(null); return; }
                            void saveField(field, newValue, value);
                          },
                          () => setEditingKey(null),
                          // Distinct-value fallback for selects: collect every
                          // non-null value of this key across cached rows.
                          field.type === "select"
                            ? cachedRows.map(r => r[field.key]).filter(v => v != null && v !== "").map(String)
                            : undefined,
                        )
                      ) : (
                        <button
                          type="button"
                          onClick={canEdit ? () => setEditingKey(field.key) : undefined}
                          disabled={!canEdit}
                          aria-label={canEdit ? `Edit ${field.label}` : undefined}
                          title={
                            isUserField
                              ? "User picker arrives in a later commit"
                              : !editable
                                ? "Preview data — not editable"
                                : "Click to edit"
                          }
                          className={`group w-full text-left -mx-2 px-2 py-1.5 rounded-md flex items-center gap-2 ${
                            canEdit
                              ? "hover:bg-slate-50 cursor-text"
                              : "cursor-default"
                          }`}
                        >
                          <span
                            className={`flex-1 min-w-0 ${
                              shouldUnderline
                                ? "underline decoration-dotted decoration-slate-200 underline-offset-4 group-hover:decoration-slate-500 transition-colors"
                                : ""
                            }`}
                          >
                            {renderField(field, row!)}
                          </span>
                          {canEdit && (
                            <Pencil
                              size={12}
                              className="text-slate-300 group-hover:text-slate-500 transition-colors shrink-0"
                            />
                          )}
                        </button>
                      )}
                      {isUserField && (
                        <p className="text-[10px] text-slate-400 italic mt-1 ml-0">
                          User picker arrives in a later commit
                        </p>
                      )}
                    </dd>
                  </div>
                );
              })}
            </dl>
          )}
        </div>

        {/* Footer — hint by default, error message after a failed save.
            Right side hosts the Delete affordance when the row is editable. */}
        <footer
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
          className={`px-5 py-3 border-t border-slate-200 text-[11px] flex items-center justify-between gap-3 ${
            saveError ? "text-rose-600 bg-rose-50" : "text-slate-400"
          }`}
        >
          <span className="min-w-0 truncate">
            {saveError
              ? saveError
              : editable
                ? "Changes auto-save · click any field to edit"
                : "Preview data — read-only"}
          </span>
          {editable && row?.id && (
            <button
              type="button"
              onClick={() => setDeleteConfirmOpen(true)}
              className="shrink-0 text-[11px] text-red-600 hover:text-red-700 hover:underline"
            >
              Delete record
            </button>
          )}
        </footer>
      </aside>

      {open && deleteConfirmOpen && row && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/45 px-4">
          <div className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start gap-4 border-b border-slate-100 p-5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-slate-900">Delete this record?</h2>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">
                  This removes "{rowTitle(row)}" from {screen?.label || "this screen"}. It cannot be undone from the Agentic Operating System.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDeleteConfirmOpen(false)}
                className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close delete confirmation"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm leading-relaxed text-red-800">
                Use this only when the record is truly no longer part of the workflow. If it should stay for audit history, change its status instead.
              </div>
            </div>
            <div className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleting}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-60"
              >
                Keep record
              </button>
              <button
                type="button"
                onClick={deleteRow}
                disabled={deleting}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-60"
              >
                {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
                Delete record
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
